<template>
  <transition name="fade">
    <div v-if="isLoadingInternal" class="overlay-wrapper">
      <div class="overlay-content">
        <span class="spinner" />
        <span class="overlay-text">{{ loadingMessage }}</span>
      </div>
    </div>
  </transition>

  <nav aria-label="Page navigation" v-show="totalRecords > 0">
    <div class="paginator-wrapper">

      <!-- Total Records -->
      <div class="paginator-item text-muted text-center">
        <span class="font-weight-bold">Total Records</span> {{ totalRecords }}
      </div>

      <!-- Selector de página Backend -->
      <div class="paginator-item" v-if="paginatorBackend">
        <ul class="pagination justify-content-center mb-0">
          <li class="page-item">
            <select class="form-control form-control-sm"
              @change="handleBlockPageChangeWithLoading($event)">
              <option v-for="n in paginatorBackend.TotalPages" :key="n" :value="n">
                Block {{ n }}
              </option>
            </select>
          </li>
          <li class="page-item">
            <select class="form-control form-control-sm"
              v-model.number="selectedPage" @change="jumpToPageWithLoading">
              <option v-for="page in totalPagesArray" :key="page" :value="page">
                Page {{ page }}
              </option>
            </select>
          </li>
        </ul>
      </div>

      <!-- Paginación frontend -->
      <div class="paginator-item">
        <ul class="pagination pagination-sm justify-content-center mb-0">

          <li class="page-item" v-if="!paginatorBackend && totalRecords > 15">
            <select class="form-control form-control-sm"
              v-model.number="selectedPage" @change="jumpToPageWithLoading">
              <option v-for="page in totalPagesArray" :key="page" :value="page">
                Page {{ page }}
              </option>
            </select>
          </li>

          <li class="page-item" :class="{ disabled: isPrevDisabled }">
            <a class="page-link text-primary" @click="prevPageWithLoading">
              <i class="fa fa-chevron-left" aria-hidden="true"></i>
            </a>
          </li>

          <li class="page-item"
            v-for="page in visiblePages"
            :key="page"
            :class="{ active: currentPage === page }">
            <a class="page-link" @click="goToPageWithLoading(page)">{{ page }}</a>
          </li>

          <li class="page-item" :class="{ disabled: isNextDisabled }">
            <a class="page-link text-primary" @click="nextPageWithLoading">
              <i class="fa fa-chevron-right" aria-hidden="true"></i>
            </a>
          </li>

          <li class="page-item" v-if="!paginatorBackend && totalRecords > 15">
            <select class="form-control form-control-sm"
              v-model.number="localItemsPerPage" @change="onItemsPerPageChangeWithLoading">
              <option v-for="n in itemsPerPageOptions" :key="n" :value="n">
                items {{ n }}
              </option>
            </select>
          </li>

        </ul>
      </div>

      <!-- From / To -->
      <div class="paginator-item text-muted text-center">
        <span class="font-weight-bold">Displayed from</span> {{ from }}
        <span class="font-weight-bold">to</span> {{ to }}
      </div>

    </div>
  </nav>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onBeforeUnmount, onMounted } from 'vue'

interface PaginationProps {
  currentPage:  number
  totalPages:   number
  prevPage:     () => void
  nextPage:     () => void
  goToPage:     (page: number) => void
  visiblePages: number[]
  totalRecords: number
  from:         number
  to:           number
  currentData?: string
  paginatorBackend?: {
    DataCount?:  number | null
    TotalPages?: number | null
    PageNumber?: number | null
    PageSize?:   number | null
  } | null
  goToPageBackend?: (pageNumber: number, current: string) => void
  itemsPerPage:     number
  setItemsPerPage?: (value: number) => void
}

const props = defineProps<PaginationProps>()

const screenSmall = ref(false)
const updateWindowSize = () => { screenSmall.value = window.innerWidth < 768 }
onMounted(() => window.addEventListener('resize', updateWindowSize))
onBeforeUnmount(() => window.removeEventListener('resize', updateWindowSize))
updateWindowSize()

const isPrevDisabled  = computed(() => props.currentPage <= 1)
const isNextDisabled  = computed(() => props.currentPage >= props.totalPages)
const totalPagesArray = computed(() => Array.from({ length: props.totalPages }, (_, i) => i + 1))
const itemsPerPageOptions = [15, 25, 50, 100, 200]

const localItemsPerPage = ref(props.itemsPerPage ?? 15)

const selectedPage = ref(props.currentPage)
watch(() => props.currentPage, (val) => { selectedPage.value = val })

const isLoadingInternal = ref(false)
const loadingMessage    = ref('Please wait, loading ...')

const withLoading = async (action: () => void | Promise<void>) => {
  isLoadingInternal.value = true
  await new Promise(resolve => setTimeout(resolve, 200))
  await action()
  isLoadingInternal.value = false
}

const prevPageWithLoading  = ()             => withLoading(props.prevPage)
const nextPageWithLoading  = ()             => withLoading(props.nextPage)
const goToPageWithLoading  = (page: number) => withLoading(() => props.goToPage(page))

const jumpToPageWithLoading = () => withLoading(() => {
  if (selectedPage.value >= 1 && selectedPage.value <= props.totalPages)
    props.goToPage(selectedPage.value)
})

const onItemsPerPageChangeWithLoading = () => withLoading(() => {
  props.setItemsPerPage?.(localItemsPerPage.value)
})

const handleBlockPageChange = (event: Event) => {
  const pageNumber = parseInt((event.target as HTMLSelectElement).value, 10)
  props.goToPageBackend?.(pageNumber, props.currentData ?? '')
}
const handleBlockPageChangeWithLoading = (event: Event) =>
  withLoading(() => handleBlockPageChange(event))
</script>

<style>
.pagination .page-link,
.pagination select,
.btn {
  cursor: pointer;
  transition: all 0.2s ease;
}
.pagination .page-link:hover,
li.btn:hover {
  background-color: rgba(0, 123, 255, 0.1);
  color: #007bff;
}

.overlay-wrapper {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.65);
  z-index: 9999;
}
.overlay-content {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 50px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1), 0 1px 4px rgba(0, 0, 0, 0.06);
}
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #e9ecef;
  border-top-color: #007bff;
  border-radius: 50%;
  animation: overlay-spin 0.6s linear infinite;
  flex-shrink: 0;
}
@keyframes overlay-spin {
  to { transform: rotate(360deg); }
}
.overlay-text {
  font-size: 13px;
  font-weight: 500;
  color: #495057;
  white-space: nowrap;
  line-height: 1;
}

.fade-enter-active,
.fade-leave-active  { transition: opacity 0.2s ease; }
.fade-enter-from,
.fade-leave-to      { opacity: 0; }

.paginator-wrapper {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-around;
  gap: 0.5rem;
}
@media (max-width: 991.98px) {
  .paginator-wrapper {
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  .paginator-wrapper .paginator-item {
    flex-direction: row;
    gap: 2px;
  }
}
</style>